import { Avatar, AvatarFallback, AvatarImage } from "@radix-ui/react-avatar";
import { BusFront, Heart, MapPin, SquareArrowOutUpRight, TrainFront, Users } from "lucide-react";
import Text from "~/components/atoms/Text";
import { Card, CardContent } from "~/components/ui/card";
import SkeletonCard from "./SkeletonCard";
import convertCurrency from "~/lib/helpers/currencyConverter";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "~/components/ui/carousel";
import { useFavorite } from "~/lib/hooks/useFavourite";
import { Link } from "@remix-run/react";
import { useCompare } from "~/lib/hooks/useCompare";
import { useCurrentCurrency } from "~/lib/helpers/CurrencyHelper";
import { cn } from "~/lib/utils";
import { useState, useEffect } from "react";
import type { CarouselApi } from "~/components/ui/carousel";
import { Skeleton } from "~/components/ui/skeleton";
import { isAvailableBeforeDays } from "~/lib/helpers/availabilityStringByDateHelper";
import TrueCheckWithToolTip from "../common/TrueCheckWithToolTip";
import { closeAllHoverTips } from "../common/HoverTip";
import { recordListingEngagement } from "~/lib/Telemetry";
import type { CardListingDisplay } from "~/lib/helpers/cardListingDisplay";
import {
  CARD_IMAGE_OVERLAY_BADGE_CLASS,
  CARD_IMAGE_OVERLAY_BADGE_TEXT_CLASS,
  formatPropertyDeskRangePillLabel,
} from "~/lib/helpers/cardListingDisplay";
import { normalizePropertyTypeLabel } from "~/lib/helpers/normalizePropertyType";
import ListingSpaceTypeChips from "./ListingSpaceTypeChips";

const CAROUSEL_IMAGE_HEIGHT_PX = 220;

export type PGridCardProps = CardListingDisplay & {
  loading: boolean;
  link: string;
  propertyLink: string;
  isFirstVisible: boolean;
  onHover?: Function;
  onHoverUpdate?: (id: string | undefined) => void;
  buildingLink?: string;
  workspaceLink?: string;
  wrapperStyle?: string;
  cardClassName?: string;
  altText?: string;
  isPropertyPage?: boolean;
  titleText?: string;
  showFavoriteButton?: boolean;
  showCompareButton?: boolean;
  isFeatured?: boolean;
  hideTransportInfo?: boolean;
  showTransportInfo?: boolean;
  isMapCard?: boolean;
  showSpaceTypeChips?: boolean;
};

export default function PGridCard({
  images,
  listingId,
  propertyId,
  workspaceId,
  buildingId,
  telemetryString,
  partnerLogo,
  showPartnerLogo,
  workspaceProviderName,
  showSpecialOfferBadge,
  offerLabel,
  buildingTitle,
  addressFormula,
  deskRangeLabel,
  spaceTypeLabel,
  spaceTypeCounts,
  closestBusStopName,
  closestBusStopTravelTime,
  closestTrainStationName,
  closestTrainStationTravelTime,
  currencyIsoCode,
  avgMonthlyCostPerWorkstation,
  priceMonthlyMin,
  priceMonthly,
  advertisedPriceMonthly,
  showDiscountedListingPrice,
  propertyTotalActiveListings,
  minDesks,
  maxDesks,
  mohLastModifiedDate,
  compareRegionSlug,
  loading,
  link,
  propertyLink,
  isFirstVisible,
  onHover,
  onHoverUpdate,
  buildingLink,
  workspaceLink,
  wrapperStyle,
  cardClassName,
  altText,
  isPropertyPage,
  titleText,
  showFavoriteButton = true,
  showCompareButton = true,
  isFeatured = false,
  hideTransportInfo = false,
  isMapCard,
  showSpaceTypeChips = false,
}: PGridCardProps) {

  const FOOTER_LABEL_CLASS = "text-[clamp(0.5rem,3.5cqw,18.9px)]";
  const FOOTER_ROW_CLASS = "text-[clamp(0.5rem,4.5cqw,0.875rem)]";
  const FOOTER_PRICE_EM_CLASS = "text-[1.35em] leading-none";
  // Define compact text classes for MapCard context
  const footerLabelClass = isMapCard
    ? "text-[20px]"
    : FOOTER_LABEL_CLASS;

  const footerRowClass = isMapCard
    ? "text-[0.75rem]"
    : FOOTER_ROW_CLASS;

  const footerPriceEmClass = isMapCard
    ? "text-[1.1em] leading-none"
    : FOOTER_PRICE_EM_CLASS;
  const { currency } = useCurrentCurrency();
  const { liked, toggleFavorite } = useFavorite(listingId);
  const [chipExpandHeightDelta, setChipExpandHeightDelta] = useState(0);
  const showFeaturedBadge = Boolean(isFeatured);

  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loadedSlides, setLoadedSlides] = useState<Set<number>>(new Set([0]));

  const deskRangeText = formatPropertyDeskRangePillLabel(
    minDesks,
    maxDesks,
    deskRangeLabel
  );

  const carouselImageHeight =
    CAROUSEL_IMAGE_HEIGHT_PX - chipExpandHeightDelta;

  const showWorkspaceLogo = !!partnerLogo && showPartnerLogo;
  const cardLink = isPropertyPage ? propertyLink : link;

  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => {
      const current = carouselApi.selectedScrollSnap();
      setCurrentSlide(current);
      setLoadedSlides((prev) => {
        const next = new Set(prev);
        next.add(current);
        if (current + 1 < images.length) next.add(current + 1);
        return next;
      });
    };
    carouselApi.on("select", onSelect);
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi, images.length]);

  const { compareCount, isCompared, toggleCompare } = useCompare(listingId);
  const isAvailable = isAvailableBeforeDays(mohLastModifiedDate);
  const [hasRecordedEngagement, setHasRecordedEngagement] = useState(false);

  const propertyPriceText =
    avgMonthlyCostPerWorkstation != null
      ? `${convertCurrency(
        avgMonthlyCostPerWorkstation ?? priceMonthlyMin ?? 0,
        currencyIsoCode,
        currency
      )} `
      : "";
  const listingPriceText =
    priceMonthly != null
      ? `${convertCurrency(priceMonthly, currencyIsoCode, currency)}`
      : "";
  const numericListingPrice = Number(
    priceMonthly != null
      ? convertCurrency(priceMonthly, currencyIsoCode, currency, true)
      : "",
  );
  const advertisedPriceText =
    advertisedPriceMonthly != null
      ? `${convertCurrency(advertisedPriceMonthly, currencyIsoCode, currency)} `
      : "";

  const recordHover = () => {
    if (onHover) onHover(listingId, propertyId);
    if (onHoverUpdate) onHoverUpdate(listingId);
  };

  const clearHover = () => {
    closeAllHoverTips();
    if (onHover) onHover(null);
    if (onHoverUpdate) onHoverUpdate(undefined);
  };
  // Handler for card-wide redirection
  const handleCardClick = (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
  ) => {
    // If triggered by keyboard, only accept Enter or Space
    if ("key" in e && e.key !== "Enter" && e.key !== " ") {
      return;
    }

    handleEngagement();
    if (cardLink) {
      window.open(cardLink, "_blank", "noopener,noreferrer");
    }
  };

  const handleEngagement = () => {
    if (!hasRecordedEngagement && listingId && propertyId) {
      recordListingEngagement(
        listingId,
        propertyId,
        workspaceId || "",
        buildingId || ""
      );
      setHasRecordedEngagement(true);
    }
  };

  return (
    <>
      {images.map((i: { url: string }, index: number) => {
        if (isFirstVisible && index === 0) {
          return (
            <link
              key={`preload-${i.url}`}
              data-spinner="true"
              href={`https://ik.imagekit.io/officehub/tr:h-800,f-avif/${i.url}`}
              as="image"
              rel="preload"
              fetchPriority="high"
            />
          );
        }
        return null;
      })}

      {loading ? (
        <SkeletonCard displayMode="grid" />
      ) : (
        <div
          className={
            wrapperStyle
              ? wrapperStyle
              : "group/card flex justify-center w-full h-full max-h-[483px] rounded-lg shadow-custom overflow-hidden "
          }
          data-otelemetry={telemetryString}
          onMouseEnter={recordHover}
          onMouseLeave={clearHover}
        >
          <Card
            role="button"
            tabIndex={0}
            aria-label={`View ${buildingTitle || addressFormula}`}
            onClick={handleCardClick}
            onKeyDown={handleCardClick}
            className={
              cardClassName
                ? cn("relative w-full cursor-pointer", cardClassName)
                : "relative w-full h-full max-h-[483px]  overflow-hidden flex flex-col cursor-pointer"
            }
          >
            <CardContent
              className="relative z-10 flex-1 min-h-0 flex flex-col p-0 overflow-hidden pointer-events-none"
            >
              {/* Image Carousel */}
              <div
                data-card-image-carousel
                className="group/carousel relative w-full shrink-0 flex-none overflow-hidden transition-[height] duration-200 ease-in-out pointer-events-auto"
                style={{
                  height: carouselImageHeight,
                  minHeight: carouselImageHeight,
                  maxHeight: carouselImageHeight,
                }}
              >
                <div className="absolute inset-0 [&_.overflow-hidden]:h-full">
                  <Carousel
                    opts={{ loop: true }}
                    setApi={setCarouselApi}
                    className="h-full w-full"
                  >
                    <CarouselContent className="h-full ml-0">
                      {images.map((i: { url: string }, index: number) => {
                        const isLCP = isFirstVisible && index === 0;
                        const shouldRender = loadedSlides.has(index);
                        return (
                          <CarouselItem
                            key={i.url}
                            className="h-full pl-0 basis-full"
                          >
                            <div className="block h-full">
                              {shouldRender ? (
                                <img
                                  src={`https://ik.imagekit.io/officehub/tr:h-500,w-800,f-auto,f-avif/${i.url}`}
                                  srcSet={[
                                    `https://ik.imagekit.io/officehub/tr:h-250,w-400,f-auto,f-avif/${i.url} 400w`,
                                    `https://ik.imagekit.io/officehub/tr:h-500,w-800,f-auto,f-avif/${i.url} 800w`,
                                  ].join(", ")}
                                  sizes="(max-width: 450px) 400px, 800px"
                                  alt={`${altText} Image ${index + 1}`}
                                  width={800}
                                  height={500}
                                  className="bg-gray-300 w-full object-cover object-center"
                                  style={{ height: carouselImageHeight }}
                                  loading={isLCP ? "eager" : "lazy"}
                                  fetchPriority={isLCP ? "high" : "low"}
                                  decoding={isLCP ? "sync" : "async"}
                                  title={titleText}
                                />
                              ) : (
                                <div
                                  className="bg-gray-200 w-full"
                                  style={{ height: carouselImageHeight }}
                                  aria-hidden="true"
                                />
                              )}
                            </div>
                          </CarouselItem>
                        );
                      })}
                    </CarouselContent>
                    {images.length > 1 && (
                      <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1.5 pointer-events-none">
                        {[0, 1, 2].map((dotIndex) => {
                          const activeDot = currentSlide % 3 === dotIndex;

                          return (
                            <span
                              key={dotIndex}
                              className={cn(
                                "h-1.5 w-1.5 rounded-full transition-all duration-200",
                                activeDot ? "w-2 bg-white" : "bg-white/60"
                              )}
                            />
                          );
                        })}
                      </div>
                    )}
                    <div
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="absolute left-2 top-1/2 -translate-y-1/2 hidden group-hover/card:flex z-20 pointer-events-auto"
                    >
                      <CarouselPrevious className="static translate-y-0" />
                    </div>

                    <div
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="absolute right-2 top-1/2 -translate-y-1/2 hidden group-hover/card:flex z-20 pointer-events-auto"
                    >
                      <CarouselNext className="static translate-y-0" />
                    </div>
                  </Carousel>
                </div>

                {/* Carousel Overlay Content */}
                <div className="absolute inset-0 z-[1] pointer-events-none">
                  {/* Top Badges */}
                  <div className="flex items-center gap-1.5 absolute top-3 left-4 max-w-[70%] flex-wrap pointer-events-none">
                    {showSpecialOfferBadge && !isPropertyPage && (
                      <div className="rounded-2xl flex items-center justify-center bg-oh-secondary pointer-events-auto">
                        <Text className="text-xs text-white py-1 px-2">
                          {offerLabel}
                        </Text>
                      </div>
                    )}
                    {showFeaturedBadge && (
                      <div className={`${CARD_IMAGE_OVERLAY_BADGE_CLASS} bg-[#23B5B5]`}>
                        <Text className={`${CARD_IMAGE_OVERLAY_BADGE_TEXT_CLASS} text-white font-medium`}>
                          Most popular
                        </Text>
                      </div>
                    )}
                  </div>

                  {/* Favorite Button */}
                  {showFavoriteButton && (
                    <button
                      type="button"
                      aria-label={
                        liked
                          ? `Remove ${buildingTitle || addressFormula} from favorites`
                          : `Add ${buildingTitle || addressFormula} to favorites`
                      }
                      className="absolute top-3 right-3 rounded-full pointer-events-auto"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleFavorite(e);
                      }}
                    >
                      <Heart
                        aria-label="Favorite button"
                        size={26}
                        color={"white"}
                        className={`${liked ? "fill-oh-secondary" : "fill-oh-black opacity-40"
                          } duration-1000`}
                      />
                    </button>
                  )}

                  {/* Bottom-Left: Offices Count Chip */}
                  {!!propertyTotalActiveListings && propertyTotalActiveListings > 0 ? (
                    <button
                      type="button"
                      className="absolute left-0 bottom-0 pointer-events-auto inline-flex items-center justify-center rounded-tr-2xl bg-black px-3 py-1.5 shadow-sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEngagement();
                        if (propertyLink) {
                          window.open(propertyLink, "_blank");
                        }
                      }}
                    >
                      <span className="inline-flex items-center justify-center gap-1.5 text-xs font-medium leading-none text-white whitespace-nowrap">
                        <span className="group-hover/card:hidden">
                          {propertyTotalActiveListings}{" "}
                          {propertyTotalActiveListings === 1 ? "Office" : "Offices"}
                        </span>
                        <span className="hidden group-hover/card:inline-flex items-center gap-1.5">
                          {propertyTotalActiveListings === 1 ? (
                            <>View Office</>
                          ) : (
                            <>
                              View all {propertyTotalActiveListings} offices
                            </>
                          )}
                          <SquareArrowOutUpRight size={12} className="flex-shrink-0" />
                        </span>
                      </span>
                    </button>
                  ) : null}

                  {/* Bottom-Right: Compare Chip */}
                  {!isPropertyPage && showCompareButton && (
                    <div
                      className="absolute right-0 bottom-0 rounded-tl-2xl bg-black px-3 py-[3px] text-white text-xs font-medium flex items-center gap-1.5 shadow-sm pointer-events-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Circle Badge: Click to add/remove from compare */}
                      <button
                        type="button"
                        aria-label={isCompared ? "Remove from compare" : "Add to compare"}
                        className={`w-4 h-4 rounded-full border border-white text-[10px] flex items-center justify-center cursor-pointer ${isCompared ? "bg-white text-black font-bold hover:bg-gray-200" : "text-white"
                          } duration-300`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCompare(e);
                        }}
                      >
                        {isCompared ? compareCount : ""}
                      </button>

                      {/* Text Label: Click to open comparison tab (when selected) or toggle compare (when not) */}
                      <button
                        type="button"
                        className={`cursor-pointer ${isCompared ? "font-bold" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isCompared) {
                            window.open(`/${compareRegionSlug}/compare`, "_blank");
                          } else {
                            toggleCompare(e);
                          }
                        }}
                      >
                        {isCompared ? "Compare Now" : "Compare"}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Card Body Content */}
              <div className="relative flex flex-1 flex-col min-h-0 pointer-events-none">
                <div className="flex flex-col gap-1.5 px-4 pt-3 pb-2 flex-shrink-0">
                  {/* Space Type Label */}
                  {spaceTypeLabel ? (
                    <span className="text-xs font-bold text-[#23B5B5] tracking-wide uppercase">
                      {normalizePropertyTypeLabel(spaceTypeLabel)}
                    </span>
                  ) : (
                    <div className="h-4" aria-hidden="true" />
                  )}

                  {/* Partner Logo + Title / Subtitle */}
                  <div
                    className={`grid min-w-0 gap-x-2.5 gap-y-1 items-start ${showWorkspaceLogo ? "grid-cols-[auto_1fr]" : "grid-cols-1"
                      }`}
                  >
                    {/* Keep workspace provider link specific to the logo */}
                    {showWorkspaceLogo && workspaceLink ? (
                      <Link
                        to={workspaceLink}
                        target="_blank"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEngagement();
                        }}
                        aria-label={`View ${workspaceProviderName || "workspace provider"}`}
                        className="pointer-events-auto row-start-1 col-start-1 flex-shrink-0 self-start"
                      >
                        <div className="w-13 h-13 rounded-full flex items-center justify-center p-[3px] bg-white border border-gray-100 shadow-sm">
                          <Avatar>
                            <AvatarImage
                              className="rounded-full w-[46px] h-[46px] object-scale-down"
                              src={`https://ik.imagekit.io/officehub/tr:h-128,f-avif/${partnerLogo}`}
                              alt={`Logo Of ${workspaceProviderName || "Partner Logo"}`}
                              title={workspaceProviderName}
                            />
                            <AvatarFallback>
                              <Skeleton className="w-9 h-9 rounded-full" />
                            </AvatarFallback>
                          </Avatar>
                        </div>
                        {/* SEO-friendly anchor text without changing the UI */}
                        <span className="sr-only">
                          {workspaceProviderName || "Workspace provider"}
                        </span>
                      </Link>
                    ) : null}

                    <div className="flex flex-col gap-0.5 w-full min-w-0">
                      <Link
                        to={cardLink}
                        target="_blank"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEngagement();
                        }}
                        className="pointer-events-auto block"
                        aria-label={`View ${buildingTitle}`}
                      >
                        <span className="text-lg font-semibold line-clamp-1 block  truncate">
                          {buildingTitle}
                        </span>
                      </Link>

                      <h3 className="flex items-center gap-1 font-medium text-sm text-gray-600 min-w-0">
                        <MapPin size={14} className="text-gray-600 shrink-0" />
                        <span className="truncate">{addressFormula}</span>
                      </h3>
                    </div>
                  </div>

                  {/* Space Type Chips */}
                  {spaceTypeCounts && isPropertyPage && !isMapCard && (
                    <div className="pointer-events-auto pt-1">
                      <ListingSpaceTypeChips
                        spaceTypeCounts={spaceTypeCounts}
                        spaceTypeLabel={spaceTypeLabel}
                        propertyLink={propertyLink}
                        onEngagement={handleEngagement}
                        onExpandHeightDeltaChange={setChipExpandHeightDelta}
                      />
                    </div>
                  )}

                  {/* Transit Information */}
                  {!hideTransportInfo &&
                    ((closestBusStopTravelTime != null && closestBusStopName) ||
                      (closestTrainStationTravelTime != null && closestTrainStationName)) ? (
                    <div className="flex flex-col gap-1.5 pt-1.5 text-xs pointer-events-auto min-w-0 w-full">
                      {closestTrainStationName && (
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0 truncate">
                            <img
                              src="/assets/icons/TrainIcon.svg"
                              alt="Train"
                              className="w-4 h-4 ml-1 shrink-0"
                            />
                            <span className="truncate text-oh-black">
                              {closestTrainStationName}
                            </span>
                          </div>

                          <span className="shrink-0 text-gray-600 font-normal whitespace-nowrap">
                            {closestTrainStationTravelTime} Min Walk
                          </span>
                        </div>
                      )}

                      {closestBusStopName && (
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0 truncate">
                            <BusFront
                              className="w-5 h-5 shrink-0"
                              strokeWidth={1.15}
                            />
                            <span className="truncate text-oh-black">
                              {closestBusStopName}
                            </span>
                          </div>

                          <span className="shrink-0 text-gray-600 font-normal whitespace-nowrap">
                            {closestBusStopTravelTime} Min Walk
                          </span>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>

                {/* Footer Section */}
                <div className="@container/footer px-4 py-3 bg-gray-50 flex-shrink-0 mt-auto min-w-0">
                  <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-end gap-x-2">
                    {/* Price Section */}
                    {isPropertyPage ? (
                      <div className="@container/price min-w-0">
                        <div className="flex flex-col">

                          {avgMonthlyCostPerWorkstation ? (
                            <span
                              className={cn(
                                "whitespace-nowrap uppercase tracking-wider text-xs text-gray-500 font-medium",
                              )}
                            >
                              From
                            </span>
                          ) : null}
                          {avgMonthlyCostPerWorkstation ? (
                            <div
                              className={cn(
                                "inline-flex min-w-0 max-w-full items-center whitespace-nowrap",
                                FOOTER_ROW_CLASS
                              )}
                            >
                              <div className="inline-flex min-w-0 items-center whitespace-nowrap">
                                <span
                                  className={cn(
                                    "font-bold text-gray-900",
                                    footerLabelClass
                                  )}
                                >
                                  {propertyPriceText}
                                </span>

                                <span className="shrink-0 pl-1 font-medium text-sm text-gray-600">
                                  desk/mth
                                </span>
                              </div>

                              {isAvailable && (
                                <TrueCheckWithToolTip
                                  date={mohLastModifiedDate}
                                  isAvailable={isAvailable}
                                  isCard={true}
                                  isMapCard={false}
                                />
                              )}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : showDiscountedListingPrice ? (
                      <div className="@container/price min-w-0">
                        <div className="flex flex-col">
                          <div
                            className={cn(
                              "inline-flex max-w-full items-end whitespace-nowrap",
                              FOOTER_ROW_CLASS
                            )}
                          >
                            <span
                              className={cn(
                                "font-bold text-oh-secondary",
                                FOOTER_PRICE_EM_CLASS
                              )}
                            >
                              {advertisedPriceText}
                            </span>
                            <span className="shrink-0 pl-1 font-medium text-oh-secondary">
                              /mth
                            </span>
                            {isAvailable && (
                              <TrueCheckWithToolTip
                                date={mohLastModifiedDate}
                                isAvailable={isAvailable}
                                isCard={true}
                                isMapCard={false}
                              />
                            )}
                          </div>
                          <span
                            className={cn(
                              "whitespace-nowrap text-xs font-medium text-gray-500 line-through",
                            )}
                          >
                            was {listingPriceText} /mth
                          </span>
                        </div>
                      </div>
                    ) : numericListingPrice > 0 ? (
                      <div className="@container/price min-w-0">
                        <div className="flex flex-col">
                          <span
                            className={cn(
                              "whitespace-nowrap uppercase tracking-wider text-xs text-gray-500 font-medium",
                            )}
                          >
                            From
                          </span>
                          <div
                            className={cn(
                              "inline-flex min-w-0 max-w-full items-center whitespace-nowrap",
                              FOOTER_ROW_CLASS
                            )}
                          >
                            <span
                              className={cn(
                                "font-bold text-gray-900",
                                footerLabelClass
                              )}
                            >
                              {listingPriceText}
                            </span>
                            <span className="shrink-0 pl-1 font-medium text-sm text-gray-600">
                              /mth
                            </span>

                            {isAvailable && (
                              <TrueCheckWithToolTip
                                date={mohLastModifiedDate}
                                isAvailable={isAvailable}
                                isCard={true}
                                isMapCard={false}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {/* Desk Info */}
                    <div className="flex flex-col items-end shrink-0">
                      <span
                        className={cn(
                          "whitespace-nowrap uppercase text-xs tracking-wider text-gray-500 font-medium",
                        )}
                      >
                        Team Size
                      </span>
                      <span className={cn("font-bold text-gray-900 tracking-wider flex items-center gap-1.5", footerLabelClass)}>
                        <Users className="shrink-0 h-[18px] w-[18px] text-[#232E36]"
                          strokeWidth={2}
                        />
                        {deskRangeText ||
                          (minDesks && maxDesks
                            ? `${minDesks}–${maxDesks}`
                            : minDesks ?? maxDesks ?? "-")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}